import React, { useState } from 'react';
import { Flag, X, Filter } from 'lucide-react';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';
import Select from '../../../ui/Select';

interface GoalCardProps {
  onClose?: () => void;
  onSave?: (goalData: GoalData) => void;
}

interface GoalData {
  hasGoal: boolean;
  eventType: string;
  eventName: string;
  conversionWindow: number;
  conversionWindowUnit: string;
  conversionTrigger: string;
}

const GoalCard: React.FC<GoalCardProps> = ({ onClose, onSave }) => {
  const [goalData, setGoalData] = useState<GoalData>({
    hasGoal: true,
    eventType: 'performs event',
    eventName: '',
    conversionWindow: 1,
    conversionWindowUnit: 'week',
    conversionTrigger: 'being sent',
  });

  const handleSave = () => {
    onSave?.(goalData);
    onClose?.();
  };

  const eventTypeOptions = [
    { value: 'performs event', label: 'performs event' },
    { value: 'visits page', label: 'visits page' },
    { value: 'clicks link', label: 'clicks link' },
  ];

  const conversionWindowUnitOptions = [
    { value: 'day', label: 'day' },
    { value: 'week', label: 'week' },
    { value: 'month', label: 'month' },
  ];

  const conversionTriggerOptions = [
    { value: 'being sent', label: 'being sent' },
    { value: 'receiving', label: 'receiving' },
    { value: 'opening', label: 'opening' },
  ];

  return (
    <div className={`block `}>
      <div className="bg-card w-[32vw] border-l-2 border-border shadow-sm rounded-r-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-success-background rounded-lg flex items-center justify-center">
              <Flag className="w-4 h-4 text-success" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Goal</h2>
              <p className="text-xs text-muted-foreground">
                Measure campaign success
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Goal Selection */}
          <div className="space-y-2">
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  setGoalData((prev) => ({ ...prev, hasGoal: true }))
                }
                className={`flex-1 flex items-center justify-center space-x-1 p-2 rounded-lg border transition-colors text-sm ${
                  goalData.hasGoal
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Flag className="w-4 h-4" />
                <span className="font-medium">Set goal</span>
              </button>
              <button
                onClick={() =>
                  setGoalData((prev) => ({ ...prev, hasGoal: false }))
                }
                className={`flex-1 flex items-center justify-center space-x-1 p-2 rounded-lg border transition-colors text-sm ${
                  !goalData.hasGoal
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <div className="w-4 h-4 rounded-full border-2 border-muted-foreground flex items-center justify-center">
                  <X className="w-2.5 h-2.5" />
                </div>
                <span className="font-medium">No goal</span>
              </button>
            </div>
          </div>

          {goalData.hasGoal && (
            <>
              {/* Conversion Criteria */}
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground text-sm">
                  Conversion criteria
                </h3>
                <p className="text-xs text-muted-foreground">
                  People meet the conversion criteria when they:
                </p>

                <div className="flex space-x-2">
                  <div className="flex-1">
                    <Select
                      options={eventTypeOptions}
                      value={goalData.eventType}
                      onChange={(e) =>
                        setGoalData((prev) => ({
                          ...prev,
                          eventType: e.target.value,
                        }))
                      }
                      className="h-10 text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="select event"
                      value={goalData.eventName}
                      onChange={(e) =>
                        setGoalData((prev) => ({
                          ...prev,
                          eventName: e.target.value,
                        }))
                      }
                      className="h-10 text-sm"
                    />
                  </div>
                </div>

                <Button
                  variant="secondary"
                  size="small"
                  className="w-full flex items-center justify-center space-x-1 text-xs h-7"
                >
                  <Filter className="w-3 h-3" />
                  <span>Add event data filter</span>
                </Button>
              </div>

              {/* Conversion Window */}
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground text-sm">
                  Conversion window
                </h3>
                <p className="text-xs text-muted-foreground">
                  A conversion is counted when a person:
                </p>

                <div className="space-y-2">
                  <div className="flex items-center space-x-1 text-xs">
                    <span className="text-foreground">
                      Achieves the goal within
                    </span>
                    <div className="w-14">
                      <Input
                        type="number"
                        value={goalData.conversionWindow}
                        onChange={(e) =>
                          setGoalData((prev) => ({
                            ...prev,
                            conversionWindow: parseInt(e.target.value) || 1,
                          }))
                        }
                        className="h-8 text-center text-xs"
                        min="1"
                      />
                    </div>
                    <div className="w-22">
                      <Select
                        options={conversionWindowUnitOptions}
                        value={goalData.conversionWindowUnit}
                        onChange={(e) =>
                          setGoalData((prev) => ({
                            ...prev,
                            conversionWindowUnit: e.target.value,
                          }))
                        }
                        className="h-8 text-xs"
                      />
                    </div>
                    <span className="text-foreground">of</span>
                    <div className="w-30">
                      <Select
                        options={conversionTriggerOptions}
                        value={goalData.conversionTrigger}
                        onChange={(e) =>
                          setGoalData((prev) => ({
                            ...prev,
                            conversionTrigger: e.target.value,
                          }))
                        }
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    any delivery from this campaign.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            size="small"
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            size="small"
            className="text-sm"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoalCard;
